import React from 'react';

interface Location {
  location_id: string;
  title: string;
  lat?: number;
  lon?: number;
  blurb?: string;
  blurb_text_array?: { text_type: string; text: string }[];
  reviews_list?: { reviews: any[] };
  recommendation_score?: number;
  google_rating?: number;
  total_recommendations?: number;
  alternative_names?: string[];
  price_range?: string;
  postal_code?: string;
  country_code?: string;
  telephone?: string;
  hpaw?: {
    address?: string;
    phone?: string;
  };
  geocode_address?: {
    country: string;
    district_or_state: string;
    city: string;
    neighborhood?: string;
    formatted_address: string;
  };
  google_main_place_type?: string;
  attachments_list?: {
    posts_attachments?: { attachment: { image_id: string; blurhash?: string } }[];
  };
  good_to_know?: {
    good_to_know_statements: string[];
  };
  also_featured_in?: { _id: string; title: string; url_id: string; gluten_free_helper_id?: string }[];
  gf_identity_data?: any;
  secondaries?: { 
    _id: string; 
    text: string; 
    creator_preview?: { 
      _id: string;
      display_name?: string;
      image?: string;
      type?: string;
      indicator?: string;
    };
    google_review_data?: {
      google_reviewer_name?: string;
      google_review_url?: string;
    };
  }[];
  selected_tags?: string[];
  menu_subtitle?: string;
  menu_title?: string;
}

interface StructuredDataProps {
  title: string;
  description: string;
  locations: Location[];
  url: string;
  isGlutenFree?: boolean;
  datePublished?: string;
}

export const StructuredData: React.FC<StructuredDataProps> = ({ 
  title, 
  description, 
  locations, 
  url,
  isGlutenFree = false,
  datePublished
}) => {
  
  // Helper function to get canonical name from alternative names or fallback to location_id
  const getCanonicalName = (location: Location): string => {
    return location.alternative_names && location.alternative_names.length > 0 
      ? location.alternative_names[0] 
      : location.location_id;
  };
  
  // Generate enhanced structured data for the list
  const listStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": title,
    "description": description,
    "url": url,
    "numberOfItems": locations.length,
    "itemListElement": locations.map((location, index) => {
      // Enhanced image object
      const imageObject = location.attachments_list?.posts_attachments?.[0]?.attachment?.image_id ? {
        "@type": "ImageObject",
        "url": `https://api.steps.me/v1/imagedashboard/${location.attachments_list.posts_attachments[0].attachment.image_id}?width=1200&height=800&reason=structured_data&app_platform=top_ten&app_version=1.0.0.0P&q=85`,
        "width": 1200,
        "height": 800,
        "caption": `${location.title} - ${isGlutenFree ? 'Gluten-Free Restaurant' : 'Restaurant'}`
      } : undefined;

      // Determine business type and cuisine
      const businessType = isGlutenFree ? "Restaurant" : 
        (location.google_main_place_type === "restaurant" || 
         location.google_main_place_type === "food" || 
         location.google_main_place_type === "meal_takeaway") ? "Restaurant" : "LocalBusiness";

      // Build cuisine array
      const cuisineTypes = [];
      if (isGlutenFree) cuisineTypes.push("Gluten-Free");
      if (location.selected_tags) {
        location.selected_tags.forEach(tag => {
          // Add common cuisine mappings
          const cuisineMap: Record<string, string> = {
            'italian': 'Italian',
            'pizza': 'Pizza',
            'asian': 'Asian',
            'mexican': 'Mexican',
            'american': 'American',
            'french': 'French',
            'chinese': 'Chinese',
            'japanese': 'Japanese',
            'thai': 'Thai',
            'indian': 'Indian',
            'mediterranean': 'Mediterranean',
            'seafood': 'Seafood',
            'steakhouse': 'Steakhouse',
            'cafe': 'Cafe',
            'bakery': 'Bakery'
          };
          
          const normalizedTag = tag.toLowerCase().trim();
          if (cuisineMap[normalizedTag]) {
            cuisineTypes.push(cuisineMap[normalizedTag]);
          }
        });
      }

      const canonicalName = getCanonicalName(location);
      
      const itemData: any = {
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": businessType,
          "@id": `${url}#location-${canonicalName}`,
          "url": isGlutenFree ? `https://www.atly.com/gluten-free/location/${canonicalName}` : `${url}#location-${canonicalName}`,
          "name": location.title,
          "description": location.blurb,
          "image": imageObject,
          "address": (location.hpaw?.address || location.geocode_address) ? {
            "@type": "PostalAddress",
            "streetAddress": location.hpaw?.address || location.geocode_address?.formatted_address,
            "addressLocality": location.geocode_address?.city,
            "addressRegion": location.geocode_address?.district_or_state,
            "addressCountry": location.country_code || location.geocode_address?.country,
            "postalCode": location.postal_code
          } : undefined,
          "telephone": location.telephone || location.hpaw?.phone
          // Removed aggregateRating to fix "multiple aggregate ratings" error
        }
      };

      // Add location coordinates
      if (location.lat && location.lon) {
        itemData.item.geo = {
          "@type": "GeoCoordinates",
          "latitude": location.lat,
          "longitude": location.lon
        };
      }

      // Add restaurant-specific properties
      if (businessType === "Restaurant") {
        itemData.item.servesCuisine = cuisineTypes.length > 0 ? cuisineTypes : undefined;
        itemData.item.priceRange = location.price_range;
        
        // Add aggregate rating from Google
        if (location.google_rating) {
          itemData.item.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": location.google_rating,
            "bestRating": 5,
            "worstRating": 1,
            "reviewCount": location.total_recommendations || 1
          };
        }
        
        // Add menu information if available
        if (location.menu_title || location.menu_subtitle) {
          itemData.item.hasMenu = true;
          if (location.menu_title) {
            itemData.item.menu = {
              "@type": "Menu",
              "name": location.menu_title,
              "description": location.menu_subtitle
            };
          }
        }

        // Add gluten-free specific properties
        if (isGlutenFree) {
          itemData.item.amenityFeature = [
            {
              "@type": "LocationFeatureSpecification",
              "name": "Gluten-Free Options",
              "value": true
            }
          ];
          
          // Add dietary restrictions
          itemData.item.knowsAbout = [
            "Gluten-Free Cuisine",
            "Celiac Disease",
            "Dietary Restrictions"
          ];
        }
      }

      // Add good to know information as additional properties
      if (location.good_to_know?.good_to_know_statements) {
        itemData.item.additionalProperty = location.good_to_know.good_to_know_statements.map(statement => ({
          "@type": "PropertyValue",
          "name": "Good to Know",
          "value": statement
        }));
      }

      return itemData;
    })
  };

  // Enhanced breadcrumb data
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Atly",
        "item": {
          "@type": "WebPage",
          "@id": "https://www.atly.com",
          "url": "https://www.atly.com"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": isGlutenFree ? "Gluten-Free Guides" : "Top 10 Guides",
        "item": {
          "@type": "WebPage",
          "@id": isGlutenFree ? "https://www.atly.com/best/gluten-free" : "https://www.atly.com/top",
          "url": isGlutenFree ? "https://www.atly.com/best/gluten-free" : "https://www.atly.com/top"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": title.replace(/ - Atly$/, ''),
        "item": {
          "@type": "WebPage",
          "@id": url,
          "url": url
        }
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(listStructuredData, null, 2)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbData, null, 2)
        }}
      />
    </>
  );
}; 